import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { FormSection, SecondaryButton } from 'components/forms';
import type React from 'react';
import type { MetaConnection } from 'services/api/relifMeta.api';
import { useGetAdAccountsByBusinessUnitQuery } from 'services/api/relifMeta.api';
import { NAVY, RADIUS_CARD, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

import ConnectionGate from './_shared/ConnectionGate';

interface AdsTabProps {
  businessUnitId: number;
  connection: MetaConnection | null;
  isLoading: boolean;
  onSwitchToConnection: () => void;
}

const accountStatusLabel = (status?: number): string => {
  if (status === 1) return 'Activa';
  if (status === 2) return 'Deshabilitada';
  if (status === 3) return 'Eliminada';
  if (status === undefined) return '—';
  return `Estado ${status}`;
};

const AdsTab: React.FC<AdsTabProps> = ({ businessUnitId, connection, isLoading, onSwitchToConnection }) => {
  const {
    data: adAccountsData,
    isFetching,
    refetch,
  } = useGetAdAccountsByBusinessUnitQuery({ businessUnitId, metaConnectionId: connection?.id }, { skip: !connection });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: NAVY }} />
      </Box>
    );
  }

  if (!connection) {
    return <ConnectionGate onSwitch={onSwitchToConnection} surface="ads" />;
  }

  const adAccounts = adAccountsData?.adAccounts ?? [];

  return (
    <Stack spacing={3}>
      <FormSection
        eyebrow="Cuentas asignadas"
        title="Cuentas publicitarias activas en esta BU"
        description="Las ad accounts se asignan automáticamente al conectar Meta. Para agregar o quitar cuentas, vuelve a la pestaña Conexión y reconecta — el popup de Meta es la pantalla de selección."
      >
        <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'flex-end', mb: 1.5 }}>
          <SecondaryButton
            onClick={() => {
              refetch().catch(() => undefined);
            }}
            disabled={isFetching}
          >
            {isFetching ? 'Refrescando…' : 'Refrescar'}
          </SecondaryButton>
        </Stack>

        <TableContainer
          sx={{ backgroundColor: SURFACE_CONTAINER_LOWEST, borderRadius: RADIUS_CARD, overflow: 'hidden' }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <HeadCell>Cuenta</HeadCell>
                <HeadCell>Meta Ad Account ID</HeadCell>
                <HeadCell>Estado</HeadCell>
                <HeadCell align="right">Moneda</HeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adAccounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} sx={{ border: 'none', py: 6 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Jost, sans-serif',
                        color: alpha(NAVY, 0.55),
                        textAlign: 'center',
                        fontSize: '0.95rem',
                      }}
                    >
                      Sin cuentas asignadas. Reconecta Meta desde la pestaña Conexión y elige las cuentas a compartir en
                      el popup.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                adAccounts.map((account) => (
                  <BodyRow key={account.id}>
                    <BodyCell weight={600}>{account.name}</BodyCell>
                    <BodyCell mono>{account.metaAdAccountId}</BodyCell>
                    <BodyCell>{accountStatusLabel(account.accountStatus)}</BodyCell>
                    <BodyCell align="right">{account.currency ?? '—'}</BodyCell>
                  </BodyRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </FormSection>
    </Stack>
  );
};

const HeadCell: React.FC<{ children: React.ReactNode; align?: 'right' }> = ({ children, align }) => (
  <TableCell
    align={align}
    sx={{
      backgroundColor: SURFACE_CONTAINER_LOW,
      border: 'none',
      fontFamily: 'Jost, sans-serif',
      fontSize: '0.6875rem',
      fontWeight: 600,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: alpha(NAVY, 0.55),
      py: 1.5,
    }}
  >
    {children}
  </TableCell>
);

const BodyRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <TableRow
    sx={{
      transition: 'background-color 0.18s ease',
      '&:hover': { backgroundColor: alpha(NAVY, 0.03) },
    }}
  >
    {children}
  </TableRow>
);

const BodyCell: React.FC<{ children: React.ReactNode; mono?: boolean; weight?: number; align?: 'right' }> = ({
  children,
  mono,
  weight = 500,
  align,
}) => (
  <TableCell
    align={align}
    sx={{
      border: 'none',
      fontFamily: mono ? '"JetBrains Mono", monospace' : 'Jost, sans-serif',
      fontSize: '0.9375rem',
      fontWeight: weight,
      color: NAVY,
      py: 1.75,
    }}
  >
    {children}
  </TableCell>
);

export default AdsTab;
